import {
  Avatar,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Link as RouterLink } from 'react-router-dom';
import getInitials from '../../utils/getInitials';

const getPrimarySession = (user) =>
  (user.trainingPlanSessions || []).find(
    (session) => session.status === 'ACTIVE'
  ) ||
  user.trainingPlanSessions?.[0] ||
  null;

function getWeeks(session) {
  const trainingPlan = session?.trainingPlan;
  const totalWeeks = Math.max(1, trainingPlan?.weekCount || 1);
  const eventCount = Math.max(1, trainingPlan?.eventCount || 1);
  const completedCount = session?.completedEvents?.length || 0;
  const eventsPerWeek = Math.max(1, Math.ceil(eventCount / totalWeeks));
  const currentWeek = Math.max(
    1,
    Math.min(totalWeeks, Math.floor(completedCount / eventsPerWeek) + 1)
  );

  return { currentWeek, totalWeeks };
}

function getAdherence(session) {
  const eventCount = session?.trainingPlan?.eventCount || 0;
  if (!eventCount) {
    return null;
  }

  return Math.min(
    100,
    Math.round(((session?.completedEvents?.length || 0) / eventCount) * 100)
  );
}

function getStatus(user) {
  const lastWorkoutAt = user.lastWorkoutSessionAt
    ? new Date(user.lastWorkoutSessionAt)
    : null;
  if (!lastWorkoutAt) {
    return {
      label: 'No workout activity',
      color: '#ef4444',
      bg: '#fee2e2',
      dot: '#ef4444',
    };
  }

  const daysSinceLastWorkout = Math.max(
    0,
    Math.floor((Date.now() - lastWorkoutAt.getTime()) / 86400000)
  );

  if (daysSinceLastWorkout <= 1) {
    return {
      label: 'Active today',
      color: '#16a34a',
      bg: '#dcfce7',
      dot: '#4ade80',
    };
  }
  if (daysSinceLastWorkout <= 3) {
    return {
      label: 'Active yesterday',
      color: '#15803d',
      bg: '#dcfce7',
      dot: '#4ade80',
    };
  }
  if (daysSinceLastWorkout <= 7) {
    return {
      label: `No activity ${daysSinceLastWorkout} days`,
      color: '#f97316',
      bg: '#ffedd5',
      dot: '#f59e0b',
    };
  }

  return {
    label: `No activity ${daysSinceLastWorkout} days`,
    color: '#ef4444',
    bg: '#fee2e2',
    dot: '#ef4444',
  };
}

function isActiveSubscription(subscription) {
  if (subscription.canceledAt) {
    return false;
  }

  if (
    subscription.expiresAt &&
    new Date(subscription.expiresAt) <= new Date()
  ) {
    return false;
  }

  return true;
}

function getSubscriptionTier(user) {
  const subscription = (user.subscriptions || []).find(isActiveSubscription);
  if (!subscription) {
    return null;
  }

  return subscription.tier || 'premium';
}

function formatSubscriptionTier(tier) {
  if (!tier) {
    return 'No subscription';
  }

  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const UserResults = ({ users }) => {
  return (
    <Stack spacing={1}>
      {users.map((customer) => {
        const session = getPrimarySession(customer);
        const trainingPlan = session?.trainingPlan;
        const status = getStatus(customer);
        const subscriptionTier = getSubscriptionTier(customer);
        const adherence = getAdherence(session);
        const eventCount = trainingPlan?.eventCount || 0;
        const weeks = getWeeks(session);
        const streak = customer.workoutActivityStreakCount || 0;
        const adherenceColor =
          adherence == null
            ? '#64748b'
            : adherence >= 75
            ? '#16a34a'
            : adherence >= 50
            ? '#f97316'
            : '#ef4444';

        return (
          <Paper
            key={customer._id}
            elevation={0}
            sx={{
              alignItems: { xs: 'flex-start', lg: 'center' },
              border: '1px solid #e4e8f0',
              borderRadius: 2,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                md: 'minmax(260px, 1.1fr) minmax(220px, 0.9fr)',
                lg: 'minmax(300px, 1.25fr) minmax(260px, 1fr) 88px 88px 100px 280px',
              },
              p: 2,
            }}
          >
            <Box alignItems="center" display="flex" minWidth={0}>
              <Box position="relative" mr={2}>
                <Avatar sx={{ height: 64, width: 64 }} src={customer.avatarUrl}>
                  {getInitials(customer.name)}
                </Avatar>
                <Box
                  sx={{
                    bgcolor: status.dot,
                    border: '3px solid #fff',
                    borderRadius: '50%',
                    bottom: 2,
                    height: 16,
                    position: 'absolute',
                    right: 1,
                    width: 16,
                  }}
                />
              </Box>
              <Box minWidth={0}>
                <Link
                  color="#0f172a"
                  component={RouterLink}
                  fontWeight={700}
                  sx={{ display: 'block' }}
                  to={`/trainer/users/${customer._id}`}
                  underline="none"
                  variant="subtitle1"
                >
                  {customer.name || customer._id}
                </Link>
                {customer.email && (
                  <Typography color="text.secondary" noWrap variant="body2">
                    {customer.email}
                  </Typography>
                )}
                <Typography color="text.secondary" variant="caption">
                  Member since {formatDate(customer.createdAt)}
                </Typography>
                <Box
                  sx={{
                    bgcolor: status.bg,
                    borderRadius: 999,
                    color: status.color,
                    display: 'table',
                    fontSize: 12,
                    fontWeight: 700,
                    mt: 0.75,
                    px: 1.2,
                    py: 0.35,
                  }}
                >
                  {status.label}
                </Box>
                <Box
                  sx={{
                    bgcolor: subscriptionTier ? '#e8f2ff' : '#f1f5f9',
                    borderRadius: 999,
                    color: subscriptionTier ? '#1d4ed8' : '#64748b',
                    display: 'table',
                    fontSize: 12,
                    fontWeight: 700,
                    mt: 0.75,
                    px: 1.2,
                    py: 0.35,
                  }}
                >
                  {formatSubscriptionTier(subscriptionTier)}
                </Box>
              </Box>
            </Box>

            <Box minWidth={0}>
              <Box alignItems="center" display="flex" mb={1}>
                <Box
                  sx={{
                    alignItems: 'center',
                    bgcolor: '#edeaff',
                    borderRadius: 1.2,
                    color: '#5b4bff',
                    display: 'inline-flex',
                    height: 28,
                    justifyContent: 'center',
                    mr: 1,
                    width: 28,
                  }}
                >
                  <FitnessCenterOutlinedIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography
                  color="#17213f"
                  fontWeight={650}
                  noWrap
                  variant="body2"
                >
                  {trainingPlan?.title || 'No active program'}
                </Typography>
              </Box>
              <Typography color="#17213f" fontWeight={600} variant="caption">
                {trainingPlan
                  ? `Week ${weeks.currentWeek} of ${weeks.totalWeeks}`
                  : 'Not enrolled'}
              </Typography>
              <LinearProgress
                sx={{
                  bgcolor: '#e7eaf1',
                  borderRadius: 99,
                  height: 4,
                  mt: 1,
                  maxWidth: 240,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#5b4bff',
                  },
                }}
                value={
                  trainingPlan
                    ? (weeks.currentWeek / weeks.totalWeeks) * 100
                    : 0
                }
                variant="determinate"
              />
            </Box>

            <Box>
              <Typography
                color={adherenceColor}
                fontWeight={800}
                variant="subtitle1"
              >
                {adherence == null ? '-' : `${adherence}%`}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                Adherence
              </Typography>
            </Box>

            <Box>
              <Typography color="#0f172a" fontWeight={800} variant="subtitle1">
                {session?.completedEvents?.length || 0} / {eventCount || '-'}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                Workouts
              </Typography>
            </Box>

            <Box>
              <Typography
                color={
                  streak > 7 ? '#16a34a' : streak === 0 ? '#ef4444' : '#f97316'
                }
                fontWeight={800}
                variant="subtitle1"
              >
                {streak} days
              </Typography>
              <Typography color="text.secondary" variant="caption">
                Current Streak
              </Typography>
            </Box>

            <Stack
              direction="row"
              justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
              spacing={1}
              sx={{ width: '100%' }}
            >
              <Button
                component={RouterLink}
                size="small"
                startIcon={<PersonOutlineIcon />}
                sx={{
                  borderColor: '#d6dce8',
                  color: '#17213f',
                  fontWeight: 700,
                  px: 2,
                }}
                to={`/trainer/users/${customer._id}`}
                variant="outlined"
              >
                View Profile
              </Button>
              <IconButton
                component={RouterLink}
                size="small"
                sx={{
                  border: '1px solid #d6dce8',
                  borderRadius: 1,
                  color: '#17213f',
                  height: 36,
                  width: 36,
                }}
                to={`/trainer/users/${customer._id}`}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default UserResults;
